import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('GA4_SERVICE');

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

class GA4Service {
  private isInitialized = false;
  private isReady = false;
  private readonly measurementId = 'G-5Z86MHK7QQ';

  init(): void {
    if (this.isInitialized) {
      // Expected in React StrictMode dev — second effect run, safe to ignore
      log.debug('GA4 init called again (no-op)');
      return;
    }

    try {
      if (typeof window.gtag === 'function') {
        this.isInitialized = true;
        this.isReady = true;
        log.info('GA4 initialized successfully');
      } else {
        log.warn('GA4 gtag not found - gtag.js may not have loaded yet');
        
        this.waitForGtag();
      }
    } catch (error) {
      log.error('Failed to initialize GA4:', error);
      throw error;
    }
  }

  private waitForGtag(): void {
    let attempts = 0;
    const maxAttempts = 10;
    const checkInterval = 100;

    const checkGtag = () => {
      attempts++;
      if (typeof window.gtag === 'function') {
        this.isInitialized = true;
        this.isReady = true;
        log.info('GA4 initialized successfully (delayed)');
      } else if (attempts < maxAttempts) {
        setTimeout(checkGtag, checkInterval);
      } else {
        log.error('GA4 gtag not available after waiting');
      }
    };

    setTimeout(checkGtag, checkInterval);
  }

  track(eventName: string, properties?: Record<string, any>): void {
    if (!this.isReady) {
      log.debug('GA4 not ready, skipping track:', eventName);
      return;
    }

    try {
      if (window.gtag) {
        window.gtag('event', eventName, properties || {});
        log.debug('Tracked event:', eventName, properties);
      }
    } catch (error) {
      log.error('Failed to track event:', eventName, error);
    }
  }

  identify(userId: string): void {
    if (!this.isReady) {
      log.debug('GA4 not ready, skipping identify:', userId);
      return;
    }

    try {
      if (window.gtag) {
        window.gtag('config', this.measurementId, {
          user_id: userId
        });
        log.debug('Identified user:', userId);
      }
    } catch (error) {
      log.error('Failed to identify user:', userId, error);
    }
  }

  setUserProperties(properties: Record<string, any>): void {
    if (!this.isReady) {
      log.debug('GA4 not ready, skipping set user properties');
      return;
    }

    try {
      if (window.gtag) {
        window.gtag('set', 'user_properties', properties);
        log.debug('Set user properties:', properties);
      }
    } catch (error) {
      log.error('Failed to set user properties:', error);
    }
  }

  trackPageView(pageTitle?: string, pagePath?: string): void {
    if (!this.isReady) {
      log.debug('GA4 not ready, skipping page view');
      return;
    }

    try {
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: pageTitle,
          page_path: pagePath || window.location.pathname
        });
        log.debug('Tracked page view:', pageTitle, pagePath);
      }
    } catch (error) {
      log.error('Failed to track page view:', error);
    }
  }

  getInitializationStatus(): boolean {
    return this.isReady;
  }

  async flushEvents(): Promise<void> {
    if (!this.isInitialized) {
      log.debug('GA4 not initialized, skipping flush');
      return;
    }
    
    return new Promise((resolve) => {
      setTimeout(() => {
        log.debug('Events flushed - safe to navigate');
        resolve();
      }, 300);
    });
  }
}

const ga4Service = new GA4Service();

export { ga4Service };
export default ga4Service;

